import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { useGetAdminDashboard, useGetAdminLoginAttackHistory } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn, formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { DollarSign, Users, CalendarCheck, Dumbbell, CalendarX, CheckCircle2, Circle, ArrowRight, ShieldAlert } from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer
} from "recharts";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SetupStep = { key: string; label: string; done: boolean; link: string };

export default function AdminDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const toastShown = useRef(false);
  const [, navigate] = useLocation();
  const [setupStatus, setSetupStatus] = useState<{ steps: SetupStep[]; completedCount: number; totalCount: number } | null>(null);
  const [attackHours, setAttackHours] = useState("48");

  useEffect(() => {
    if (toastShown.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true") {
      toastShown.current = true;
      toast({ title: "Upgrade complete", description: "Your gym has been upgraded to Premium." });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);

  useEffect(() => {
    apiFetch("/admin/setup-status").then(setSetupStatus).catch(() => {});
  }, []);
  
  const { data: dashboard, isLoading } = useGetAdminDashboard({
    query: { retry: false }
  });

  const { data: attackHistory, isLoading: isAttackLoading } = useGetAdminLoginAttackHistory(
    { hours: parseInt(attackHours) },
    { query: { retry: false } }
  );

  const attackChartData = (attackHistory ?? []).map(entry => ({
    label: format(new Date(entry.windowStart), "MMM d ha"),
    blocked: entry.blockedCount,
  }));

  const totalBlocked = (attackHistory ?? []).reduce((sum, e) => sum + e.blockedCount, 0);

  const recentSessions = dashboard?.recentSessions ?? [];

  const chartData = (() => {
    if (!recentSessions.length) return [];
    const dayTotals: Record<string, number> = {};
    const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    dayOrder.forEach(d => { dayTotals[d] = 0; });
    for (const s of recentSessions) {
      if (s.status === "COMPLETED" && s.proposedStart) {
        const date = new Date(s.proposedStart);
        const dayName = dayOrder[date.getDay()];
        dayTotals[dayName] += s.priceTotal ?? 0;
      }
    }
    return dayOrder.map(name => ({ name, revenue: dayTotals[name] }));
  })();

  const hasChartData = chartData.some(d => d.revenue > 0);

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        
        {/* Header */}
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Gym Overview</h1>
          <p className="text-muted-foreground text-lg">Performance metrics for your academy.</p>
        </div>

        {setupStatus && setupStatus.completedCount < setupStatus.totalCount && (
          <Card className="glass-panel border-primary/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-display text-white">Setup Checklist</CardTitle>
                <span className="text-sm font-mono text-primary">{setupStatus.completedCount}/{setupStatus.totalCount}</span>
              </div>
              <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden mt-2">
                <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${(setupStatus.completedCount / setupStatus.totalCount) * 100}%` }} />
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {setupStatus.steps.map((step) => (
                <button
                  key={step.key}
                  onClick={() => !step.done && navigate(step.link)}
                  className={cn(
                    "flex items-center gap-3 w-full text-left p-3 rounded-lg transition-colors",
                    step.done ? "opacity-60" : "hover:bg-white/5 cursor-pointer"
                  )}
                >
                  {step.done ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  <span className={cn("text-sm flex-1", step.done ? "text-muted-foreground line-through" : "text-white")}>{step.label}</span>
                  {!step.done && <ArrowRight className="w-4 h-4 text-primary shrink-0" />}
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* KPIs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <DollarSign className="w-16 h-16 text-primary" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Revenue (MTD)</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {isLoading ? "..." : formatCurrency(dashboard?.totalRevenue ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                From completed sessions
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <CalendarCheck className="w-16 h-16 text-white" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Completed Sessions</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {isLoading ? "..." : (dashboard?.completedSessions ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                {dashboard?.pendingSessions ?? 0} upcoming this week
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Users className="w-16 h-16 text-white" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Active Students</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {isLoading ? "..." : (dashboard?.studentCount ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                Across {dashboard?.activeMemberships ?? 0} memberships
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Dumbbell className="w-16 h-16 text-white" />
            </div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Active Trainers</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">
                {isLoading ? "..." : (dashboard?.trainerCount ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                Teaching private sessions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts & Lists Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Revenue Chart */}
          <Card className="glass-panel border-white/5 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Revenue This Week</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] w-full">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : !hasChartData ? (
                  <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                    <CalendarX className="w-10 h-10 opacity-50" />
                    <p className="text-sm">No revenue data yet.</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                      <XAxis dataKey="name" stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                      <RechartsTooltip 
                        cursor={{ fill: '#1e1e24' }}
                        contentStyle={{ backgroundColor: '#13131a', borderColor: '#27272e', borderRadius: '8px', color: '#fff' }}
                        itemStyle={{ color: '#e8c848', fontWeight: 'bold' }}
                      />
                      <Bar dataKey="revenue" fill="#e8c848" radius={[4, 4, 0, 0]} maxBarSize={50} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Recent Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isLoading ? (
                  <div className="animate-pulse space-y-4">
                    {[1,2,3].map(i => <div key={i} className="h-16 bg-white/5 rounded-lg" />)}
                  </div>
                ) : recentSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                    <CalendarX className="w-10 h-10 opacity-50" />
                    <p className="text-sm">No sessions yet.</p>
                  </div>
                ) : (
                  recentSessions.map((session) => {
                    const studentName = session.student
                      ? `${session.student.firstName} ${session.student.lastName.charAt(0)}.`
                      : "Student";
                    const trainerName = session.trainer
                      ? `${session.trainer.firstName} ${session.trainer.lastName.charAt(0)}.`
                      : "Trainer";
                    const initials = session.student
                      ? `${session.student.firstName.charAt(0)}${session.student.lastName.charAt(0)}`.toUpperCase()
                      : "??";
                    const timeLabel = session.proposedStart
                      ? format(new Date(session.proposedStart), "h:mm a")
                      : "";

                    return (
                      <div key={session.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-sm font-bold border border-white/10">
                            {initials}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-white">{studentName}</p>
                            <p className="text-xs text-muted-foreground">{trainerName} • {session.mode}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono text-white">{timeLabel}</p>
                          <p className={cn(
                            "text-[10px] font-bold uppercase tracking-wider",
                            session.status === 'COMPLETED' ? "text-emerald-500" :
                            session.status === 'CONFIRMED' ? "text-primary" : "text-amber-500"
                          )}>{session.status}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Login Attack History */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-400" />
                <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Login Attack History</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                {!isAttackLoading && totalBlocked > 0 && (
                  <span className="text-xs font-mono bg-rose-500/20 text-rose-300 border border-rose-500/30 px-2 py-1 rounded-full">
                    {totalBlocked} IPs blocked
                  </span>
                )}
                <Select value={attackHours} onValueChange={setAttackHours}>
                  <SelectTrigger className="w-32 h-8 text-xs bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24">Last 24h</SelectItem>
                    <SelectItem value="48">Last 48h</SelectItem>
                    <SelectItem value="72">Last 72h</SelectItem>
                    <SelectItem value="168">Last 7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <CardDescription className="text-muted-foreground text-xs mt-1">
              Blocked IPs per hour from failed login attempts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] w-full">
              {isAttackLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : attackChartData.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                  <ShieldAlert className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No blocked IPs in this window.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={attackChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#a1a1aa"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      stroke="#a1a1aa"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      cursor={{ fill: '#1e1e24' }}
                      contentStyle={{ backgroundColor: '#13131a', borderColor: '#27272e', borderRadius: '8px', color: '#fff' }}
                      itemStyle={{ color: '#f43f5e', fontWeight: 'bold' }}
                      formatter={(value: number) => [`${value} IPs blocked`, '']}
                    />
                    <Bar dataKey="blocked" fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    </AppLayout>
  );
}
